import { useEffect, useMemo, useRef, useState } from 'react';
import {
  listDeals,
  markPay,
  moveStageChecked,
  restoreDeal,
  type DealListItem,
  type DealsList,
  type StageBlock,
} from './deals-api';
import { DealModal } from './DealModal';
import { StageBlockModal } from './StageBlockModal';
import { NewDealModal } from './NewDealModal';
import { StagesModal } from './StagesModal';
import { mln } from './format';
import { useUser } from './user-context';
import { downloadCSV, printRegister, registerMoney, type Col } from './export-utils';
import { useData, invalidate } from './useData';

function payClass(d: DealListItem): string {
  if (d.stage >= 3 && d.paid === 0) return 'bad';
  const pct = d.sum ? Math.round((d.paid / d.sum) * 100) : 0;
  return pct >= 100 ? 'ok' : pct > 0 ? 'warn' : '';
}

type PayFilter = 'all' | 'unpaid' | 'partial' | 'paid' | 'risk';
const PAY_OPTS: { v: PayFilter; t: string }[] = [
  { v: 'all', t: 'Оплата: любая' },
  { v: 'risk', t: 'Риск оплаты' },
  { v: 'unpaid', t: 'Не оплачено' },
  { v: 'partial', t: 'Частично' },
  { v: 'paid', t: 'Оплачено' },
];

function matchesPay(d: DealListItem, f: PayFilter): boolean {
  if (f === 'all') return true;
  if (f === 'risk') return payClass(d) === 'bad';
  if (f === 'paid') return d.sum > 0 && d.paid >= d.sum;
  if (f === 'partial') return d.paid > 0 && d.paid < d.sum;
  return d.paid === 0; // unpaid
}

const rem = (d: DealListItem) => (d.sum != null && d.paid != null ? d.sum - d.paid : null);
const DEAL_COLS: Col<DealListItem>[] = [
  { header: 'Номер', value: (d) => d.no },
  { header: 'Клиент', value: (d) => d.client },
  { header: 'Регион', value: (d) => d.region ?? '' },
  { header: 'Этап', value: (d) => d.stage_name },
  { header: 'Сумма, ₸', value: (d) => d.sum ?? '', print: (d) => registerMoney(d.sum), align: 'r' },
  {
    header: 'Оплачено, ₸',
    value: (d) => d.paid ?? '',
    print: (d) => registerMoney(d.paid),
    align: 'r',
  },
  {
    header: 'Остаток, ₸',
    value: (d) => rem(d) ?? '',
    print: (d) => registerMoney(rem(d)),
    align: 'r',
  },
];

export function DealsScreen() {
  const user = useUser();
  const { data, refresh } = useData('deals', listDeals);
  const [openId, setOpenId] = useState<number | null>(null);
  const [openTab, setOpenTab] = useState<'items' | 'pay'>('items');
  // блокировка перехода (аванс/просрочка) — показываем отдельным окном
  const [block, setBlock] = useState<{
    block: StageBlock;
    dealId: number;
    dealNo: string;
    targetStage: number;
    fromStage: number;
  } | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const [stagesOpen, setStagesOpen] = useState(false);
  const [drag, setDrag] = useState<number | null>(null); // id перетаскиваемой сделки
  const [over, setOver] = useState<number | null>(null); // индекс колонки под курсором
  const [msg, setMsg] = useState<string | null>(null);
  const [view, setView] = useState<'funnel' | 'archive'>('funnel');
  const [arch, setArch] = useState<DealsList | null>(null);
  const [archBusy, setArchBusy] = useState(false);
  const [q, setQ] = useState('');
  const [stageFilter, setStageFilter] = useState<number | 'all'>('all');
  const [region, setRegion] = useState('all');
  const [pay, setPay] = useState<PayFilter>('all');
  const canEdit = user.screens.includes('deals');
  const isAdmin = user.isAdmin;

  // Список регионов из данных (для фильтра).
  const regions = useMemo(() => {
    if (!data) return [];
    return [...new Set(data.deals.map((d) => d.region).filter((r): r is string => !!r))].sort(
      (a, b) => a.localeCompare(b, 'ru'),
    );
  }, [data]);

  // Сделки после поиска и всех фильтров (включая этап — чтобы выгрузка/реестр
  // совпадали с тем, что видно на доске).
  const filtered = useMemo(() => {
    if (!data) return [];
    const needle = q.trim().toLowerCase();
    return data.deals.filter((d) => {
      if (stageFilter !== 'all' && d.stage !== stageFilter) return false;
      if (!matchesPay(d, pay)) return false;
      if (region !== 'all' && d.region !== region) return false;
      if (needle) {
        const hay = `${d.client} ${d.no} ${d.region ?? ''}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [data, q, pay, region, stageFilter]);

  // Авто-скролл канбана при подтаскивании карточки к краю.
  const kanbanRef = useRef<HTMLDivElement>(null);
  const pointerX = useRef(0);
  const draggingRef = useRef(false);
  const rafRef = useRef<number | null>(null);

  function autoScrollTick() {
    const el = kanbanRef.current;
    if (!el || !draggingRef.current) {
      rafRef.current = null;
      return;
    }
    const r = el.getBoundingClientRect();
    const EDGE = 90;
    const dLeft = pointerX.current - r.left;
    const dRight = r.right - pointerX.current;
    if (dLeft < EDGE) el.scrollLeft -= Math.ceil(((EDGE - dLeft) / EDGE) * 22);
    else if (dRight < EDGE) el.scrollLeft += Math.ceil(((EDGE - dRight) / EDGE) * 22);
    rafRef.current = requestAnimationFrame(autoScrollTick);
  }
  function startAutoScroll() {
    draggingRef.current = true;
    if (rafRef.current == null) rafRef.current = requestAnimationFrame(autoScrollTick);
  }
  function stopAutoScroll() {
    draggingRef.current = false;
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }
  useEffect(() => () => stopAutoScroll(), []);

  async function onCreated(id: number) {
    setNewOpen(false);
    await refresh();
    setOpenId(id);
  }

  async function drop(targetStage: number) {
    const id = drag;
    stopAutoScroll();
    setDrag(null);
    setOver(null);
    if (id == null) return;
    const card = data?.deals.find((d) => d.id === id);
    if (!card || card.stage === targetStage) return;
    setMsg(null);
    try {
      // передаём текущий этап карточки — сервер отклонит, если её уже переместил другой.
      const res = await moveStageChecked(id, targetStage, card.stage);
      if (res.status === 'blocked') {
        // аванс/просрочка — показываем понятное окно вместо мелкой строки
        setBlock({
          block: res.block,
          dealId: id,
          dealNo: card.no,
          targetStage,
          fromStage: card.stage,
        });
        await refresh();
      } else if (res.status === 'conflict') {
        setMsg('Карточку уже переместил другой пользователь — обновлено.');
        await refresh();
      } else {
        await refresh();
      }
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Этап не изменён.');
      await refresh();
    }
  }

  // Принять аванс из окна блокировки и повторить перемещение.
  async function acceptAdvanceAndMove() {
    if (!block?.block.advance) return;
    await markPay(block.dealId, block.block.advance.pay_id, {});
    const res = await moveStageChecked(block.dealId, block.targetStage, block.fromStage);
    setBlock(null);
    await refresh();
    if (res.status !== 'ok') setMsg('Аванс принят. Обновите, если этап не сдвинулся.');
  }

  async function openArchive() {
    setView('archive');
    setArchBusy(true);
    try {
      setArch(await listDeals(true));
    } finally {
      setArchBusy(false);
    }
  }

  async function doRestore(id: number) {
    setArchBusy(true);
    setMsg(null);
    try {
      await restoreDeal(id);
      const [, a] = await Promise.all([refresh(), listDeals(true)]);
      setArch(a);
      invalidate(); // счётчики меню и другие экраны учитывают восстановлённую сделку
    } catch {
      setMsg('Не удалось восстановить сделку.');
    } finally {
      setArchBusy(false);
    }
  }

  if (!data) return <p className="muted">Загрузка…</p>;

  function filterNote(): string {
    const parts: string[] = [];
    if (q.trim()) parts.push(`поиск «${q.trim()}»`);
    if (stageFilter !== 'all' && data) parts.push(`этап: ${data.stages[stageFilter]}`);
    const p = PAY_OPTS.find((o) => o.v === pay);
    if (pay !== 'all' && p) parts.push(p.t);
    if (region !== 'all') parts.push(`регион: ${region}`);
    return parts.length ? 'Фильтр: ' + parts.join(', ') : 'Все сделки';
  }
  function exportCSV() {
    downloadCSV('сделки', DEAL_COLS, filtered);
  }
  function printReg() {
    const tSum = filtered.reduce((s, d) => s + (d.sum ?? 0), 0);
    const tPaid = filtered.reduce((s, d) => s + (d.paid ?? 0), 0);
    printRegister({
      title: 'Реестр сделок',
      subtitle: filterNote(),
      cols: DEAL_COLS,
      rows: filtered,
      totals: [
        'Итого',
        '',
        '',
        '',
        registerMoney(tSum),
        registerMoney(tPaid),
        registerMoney(tSum - tPaid),
      ],
    });
  }

  return (
    <div className="scr">
      <div className="scr-head">
        <h2>Сделки</h2>
        <span className="muted">
          {view === 'archive'
            ? `${arch?.deals.length ?? 0} в архиве`
            : filtered.length === data.deals.length
              ? `${data.deals.length} контрактов`
              : `${filtered.length} из ${data.deals.length}`}
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          {view === 'archive' ? (
            <button className="btn ghost sm" onClick={() => setView('funnel')}>
              ← К воронке
            </button>
          ) : (
            <>
              <button
                className="btn ghost sm"
                disabled={filtered.length === 0}
                onClick={exportCSV}
                title="Скачать CSV (Excel)"
              >
                ⬇ CSV
              </button>
              <button
                className="btn ghost sm"
                disabled={filtered.length === 0}
                onClick={printReg}
                title="Печатный реестр"
              >
                🖨 Реестр
              </button>
              {canEdit && isAdmin && (
                <button
                  className="btn ghost sm"
                  onClick={() => setStagesOpen(true)}
                  title="Настройка этапов"
                >
                  ⚙️ Этапы
                </button>
              )}
              {data.archivedCount > 0 && (
                <button
                  className="btn ghost sm"
                  onClick={openArchive}
                  title="Архив сделок (мягко удалённые)"
                >
                  🗄 Архив · {data.archivedCount}
                </button>
              )}
              {canEdit && (
                <button className="btn sm" onClick={() => setNewOpen(true)}>
                  + Новая сделка
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {view === 'funnel' && (
        <>
          <div className="filters">
            <input
              className="filter-search"
              type="search"
              placeholder="Поиск: клиент, номер, регион…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <select
              value={String(stageFilter)}
              onChange={(e) =>
                setStageFilter(e.target.value === 'all' ? 'all' : Number(e.target.value))
              }
            >
              <option value="all">Все этапы</option>
              {data.stages.map((s, i) => (
                <option key={i} value={i}>
                  {s}
                </option>
              ))}
            </select>
            <select value={pay} onChange={(e) => setPay(e.target.value as PayFilter)}>
              {PAY_OPTS.map((o) => (
                <option key={o.v} value={o.v}>
                  {o.t}
                </option>
              ))}
            </select>
            {regions.length > 0 && (
              <select value={region} onChange={(e) => setRegion(e.target.value)}>
                <option value="all">Все регионы</option>
                {regions.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            )}
            {(q || stageFilter !== 'all' || pay !== 'all' || region !== 'all') && (
              <button
                className="btn ghost sm"
                onClick={() => {
                  setQ('');
                  setStageFilter('all');
                  setPay('all');
                  setRegion('all');
                }}
              >
                Сбросить
              </button>
            )}
          </div>

          {msg && <p className="err">{msg}</p>}
          {canEdit && (
            <p className="muted small" style={{ marginBottom: 8 }}>
              Перетаскивайте карточки между этапами.
            </p>
          )}

          <div
            className="kanban"
            ref={kanbanRef}
            onDragOver={(e) => {
              if (drag != null) {
                pointerX.current = e.clientX;
              }
            }}
          >
            {data.stages.map((stage, si) => {
              if (stageFilter !== 'all' && stageFilter !== si) return null;
              const cards = filtered.filter((d) => d.stage === si);
              return (
                <div
                  className={'kcol' + (over === si ? ' drop-over' : '')}
                  key={si}
                  onDragOver={(e) => {
                    if (drag != null) {
                      e.preventDefault();
                      if (over !== si) setOver(si);
                    }
                  }}
                  onDragLeave={() => over === si && setOver(null)}
                  onDrop={() => drop(si)}
                >
                  <div className="kcol-h">
                    <span>{stage}</span>
                    <span className="cnt">{cards.length}</span>
                  </div>
                  {cards.map((d) => (
                    <div
                      className={'kcard' + (drag === d.id ? ' dragging' : '')}
                      key={d.id}
                      draggable={canEdit}
                      onDragStart={() => {
                        setDrag(d.id);
                        const r = kanbanRef.current?.getBoundingClientRect();
                        pointerX.current = r ? (r.left + r.right) / 2 : 0; // нейтраль до первого dragOver
                        startAutoScroll();
                      }}
                      onDragEnd={() => {
                        stopAutoScroll();
                        setDrag(null);
                        setOver(null);
                      }}
                      onClick={() => setOpenId(d.id)}
                    >
                      <div className="kno">{d.no}</div>
                      <div className="kcli">{d.client}</div>
                      {d.sum != null && <div className="ksum">{mln(d.sum)} ₸</div>}
                      <div className="threads" title="нити исполнения">
                        <div className={'thread ' + payClass(d)} />
                        <div className={'thread ' + (d.stage >= 4 ? 'warn' : '')} />
                        <div className={'thread ' + (d.otk ? 'ok' : '')} />
                        <div
                          className={
                            'thread ' + (d.stage >= 7 ? 'ok' : d.stage === 6 ? 'warn' : '')
                          }
                        />
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </>
      )}

      {view === 'archive' && (
        <>
          {msg && <p className="err">{msg}</p>}
          <p className="muted small" style={{ marginBottom: 8 }}>
            Мягко удалённые сделки. В воронке и отчётах не участвуют; восстановление возвращает
            сделку на её этап.
          </p>
          {archBusy && !arch ? (
            <p className="muted">Загрузка…</p>
          ) : arch && arch.deals.length > 0 ? (
            <div className="tablewrap glass">
              <table className="tbl card-table">
                <thead>
                  <tr>
                    <th>Номер</th>
                    <th>Клиент</th>
                    <th>Этап при архивации</th>
                    <th>В архиве с</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {arch.deals.map((d) => (
                    <tr key={d.id} style={{ cursor: 'pointer' }} onClick={() => setOpenId(d.id)}>
                      <td data-label="Номер" className="mono">
                        {d.no}
                      </td>
                      <td data-label="Клиент">
                        <b>{d.client}</b>
                        {d.region && <div className="rmeta">{d.region}</div>}
                      </td>
                      <td data-label="Этап">{d.stage_name}</td>
                      <td data-label="В архиве с" className="mono muted">
                        {d.archived_at?.slice(0, 10) ?? ''}
                      </td>
                      <td className="r" onClick={(e) => e.stopPropagation()}>
                        {canEdit && (
                          <button
                            className="btn ghost sm"
                            disabled={archBusy}
                            onClick={() => doRestore(d.id)}
                          >
                            ↩ Восстановить
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="muted">Архив пуст.</p>
          )}
        </>
      )}

      {newOpen && <NewDealModal onClose={() => setNewOpen(false)} onCreated={onCreated} />}
      {block && (
        <StageBlockModal
          block={block.block}
          dealNo={block.dealNo}
          canPay={user.screens.includes('pay')}
          onClose={() => setBlock(null)}
          onOpenPay={() => {
            const dealId = block.dealId;
            setBlock(null);
            setOpenTab('pay');
            setOpenId(dealId);
          }}
          onAcceptAdvance={user.screens.includes('pay') ? acceptAdvanceAndMove : undefined}
        />
      )}
      {openId != null && (
        <DealModal
          dealId={openId}
          screens={user.screens}
          initialTab={openTab}
          onClose={() => {
            setOpenId(null);
            setOpenTab('items');
          }}
          onChanged={() => {
            refresh();
            if (view === 'archive')
              listDeals(true)
                .then(setArch)
                .catch(() => {});
          }}
        />
      )}
      {stagesOpen && (
        <StagesModal
          onClose={() => setStagesOpen(false)}
          onChanged={() => {
            invalidate('deals');
            refresh();
          }}
        />
      )}
    </div>
  );
}
